import React, { useState, useEffect, useRef } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Modal, Typography, Button, ColorCircularProgress, CardRow, Tooltip, SplitButton } from 'components'
import { colors, CONSTANTS } from 'constants/index'
import { useDispatch, useSelector } from 'react-redux'
import { getClientCaseDetails, updateClientCaseStatus } from 'store/actions/cases'
import {
  getClientPaymentSecret,
  getUserCards,
  getBankAccounts,
  getBankAccountSetupIntent,
  deleteUserCard,
  getUserCard,
  saveUserCard,
  updateUserCard,
  updateBankAccount,
  deleteBankAccount,
  setBankAccountDefault,
} from 'store/actions/payments'
import { Icon } from 'components'
import classNames from 'classnames'
import ConfigureCardModal from './ConfigureCardModal'
import AddBankAccount from './ACHform'
import BankRow from './BankRow'

const useStyles = makeStyles({
  modal: {
    display: 'flex',
    width: 800,
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    backgroundColor: colors.WHITE,
    borderRadius: 4,
    padding: 0,
    width: 800,
    maxHeight: '90vh',
    overflow: 'auto',
    position: 'relative',
  },
  paperLoader: {
    backgroundColor: colors.WHITE,
    borderRadius: 4,
    padding: 0,
    maxHeight: '90vh',
    overflow: 'auto',
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 1,
    padding: 8,
    minWidth: 'auto',
  },
  content: {
    padding: '24px 60px 24px 60px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: 500,
    color: colors.BASIC.COLOR[100],
    lineHeight: '28px',
  },
  statusBadge: {
    padding: '4px 8px',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  statusPending: {
    backgroundColor: '#ebf6fe',
    color: '#0080db',
  },
  statusActive: {
    backgroundColor: '#e8f5e8',
    color: '#4caf50',
  },
  statusRejected: {
    backgroundColor: '#ffe8e8',
    color: '#f71305',
  },
  body: {
    display: 'flex',
    gap: 24,
    marginBottom: 16,
  },
  leftSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  rightSection: {
    flex: 1,
    display: 'flex',
    gap: 8,
  },
  infoColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  infoRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  label: {
    fontSize: 13,
    color: colors.BASIC.COLOR[50],
    lineHeight: '19px',
  },
  value: {
    fontSize: 14,
    color: colors.BASIC.COLOR[100],
    lineHeight: '18px',
  },
  paymentMethodsSection: {
    marginBottom: 16,
  },
  paymentMethodsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  paymentMethodsTitle: {
    fontSize: 16,
    fontWeight: 500,
    color: colors.BASIC.COLOR[100],
    lineHeight: '28px',
  },
  addButtons: {
    display: 'flex',
    gap: 12,
  },
  addButton: {
    height: 40,
    minHeight: 32,
    fontSize: 11,
    marginLeft: 12,
  },
  splitButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    padding: '8px 12px 8px 16px',
    border: `1px solid ${colors.PRIMARY.COLOR[50]}`,
    color: colors.PRIMARY.COLOR[50],
    backgroundColor: 'transparent',
    borderRadius: 4,
    cursor: 'pointer',
    position: 'relative',
    '&:hover': {
      backgroundColor: colors.PRIMARY.COLOR[50],
      color: colors.WHITE,
    },
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: colors.WHITE,
    border: `1px solid ${colors.BASIC.COLOR[10]}`,
    borderRadius: 4,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    zIndex: 1000,
    marginTop: 4,
  },
  dropdownItem: {
    padding: '8px 16px',
    fontSize: 11,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    cursor: 'pointer',
    color: colors.BASIC.COLOR[100],
    '&:hover': {
      backgroundColor: colors.BASIC.COLOR[5],
    },
    '&:first-child': {
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
    },
    '&:last-child': {
      borderBottomLeftRadius: 4,
      borderBottomRightRadius: 4,
    },
  },
  chevronIcon: {
    fontSize: 16,
  },
  actionButtons: {
    display: 'flex',
    gap: 24,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  statusMessage: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '16px 0',
  },
  statusMessageText: {
    fontSize: 14,
    color: colors.BASIC.COLOR[70],
    fontStyle: 'italic',
  },
  declineButton: {
    backgroundColor: '#f71305',
    color: colors.WHITE,
    fontSize: 13,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    padding: '12px 24px 12px 16px',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    '&:hover': {
      backgroundColor: '#d10e04',
    },
    '&:disabled': {
      backgroundColor: '#ccc',
      color: '#666',
      cursor: 'not-allowed',
    },
  },
  approveButton: {
    backgroundColor: '#4caf50',
    color: colors.WHITE,
    fontSize: 13,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    padding: '12px 24px 12px 16px',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    '&:hover': {
      backgroundColor: '#45a049',
    },
    '&:disabled': {
      backgroundColor: '#ccc',
      color: '#666',
      cursor: 'not-allowed',
    },
  },
  buttonIcon: {
    fontSize: 16,
  },
  cards: {
    padding: 25,
    marginBottom: 24,
    border: `1px solid ${colors.BASIC.COLOR[5]}`,
    borderRadius: 4,
    backgroundColor: colors.WHITE,
  },
  noPaymentMethodsText: {
    padding: 25,
    marginBottom: 24,
    border: `1px solid ${colors.BASIC.COLOR[5]}`,
    borderRadius: 4,
    backgroundColor: colors.WHITE,
    textAlign: 'center',
    fontSize: 14,
    color: colors.BASIC.COLOR[70],
    fontStyle: 'italic',
  },
  manageIconButton: {
    minWidth: 'auto',
    padding: '5px 10px 5px 0',
    borderLeft: 'none',
    '&:hover': {
      borderLeft: 'none',
    },
  },
  tooltip: {
    padding: 16,
    backgroundColor: colors.BASIC.COLOR[80],
    color: colors.WHITE,
    fontSize: 13,
    fontWeight: 'normal',
  },
  manageInnerButtons: {
    height: 40,
    minHeight: 32,
    fontSize: 11,
    padding: '5px 10px',
  },
})

const getStatusLabel = status => {
  const statusLabels = {
    pending: 'Pending Approval',
    active: 'Approved',
    rejected: 'Declined',
    inactive: 'Inactive',
    stopped: 'Stopped',
  }
  return statusLabels[status] || 'Unknown Status'
}

const ClientPaymentApprovalModal = ({ isOpen, onClose, caseId }) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const { clientCaseDetails, clientCaseDetailsIsLoading, clientCaseStatusIsUpdating } = useSelector(
    state => state.cases
  )
  const { userCards, bankAccounts, cardsAreLoading, clientPaymentSecretKey, clientSecretIsLoading } = useSelector(
    state => state.payments
  )

  const [isModalShown, setIsModalShown] = useState(false)
  const [isACHModalShown, setIsACHModalShown] = useState(false)
  const [selectedBankType, setSelectedBankType] = useState(null)
  const [hasError, setHasError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [cardData, setCardData] = useState({ label: '', isDefault: false, remember: true })
  const [isBankDropdownOpen, setIsBankDropdownOpen] = useState(false)
  const [sortedCards, setSortedCards] = useState([])
  const [sortedBanks, setSortedBanks] = useState([])
  const dropdownRef = useRef(null)

  useEffect(() => {
    if (isOpen && caseId) {
      setIsLoading(true)
      setHasError(false)
      dispatch(getClientCaseDetails(caseId))
        .then(() => {
          // Success - modal will show
        })
        .catch(() => {
          setHasError(true)
          onClose()
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [isOpen, caseId])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsLoading(false)
      setHasError(false)
      setIsBankDropdownOpen(false)
    }
  }, [isOpen])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (isBankDropdownOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsBankDropdownOpen(false)
      }
    }

    if (isBankDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isBankDropdownOpen])

  // Sort cards and banks when data changes
  useEffect(() => {
    if (userCards) {
      const sorted = [...userCards].sort((a, b) => b.isDefault - a.isDefault)
      setSortedCards(sorted)
    }
  }, [userCards])

  useEffect(() => {
    if (bankAccounts) {
      const sorted = [...bankAccounts].sort((a, b) => b.is_default - a.is_default)
      setSortedBanks(sorted)
    }
  }, [bankAccounts])

  const handleAddCard = () => {
    dispatch(getClientPaymentSecret())
    setIsModalShown(true)
  }

  const handleAddBankAccount = bankType => {
    setSelectedBankType(bankType)
    setIsACHModalShown(true)
    setIsBankDropdownOpen(false)
  }

  const handleBankDropdownToggle = () => {
    setIsBankDropdownOpen(!isBankDropdownOpen)
  }

  const handleCardModalClose = () => {
    setIsModalShown(false)
  }

  const handleACHModalClose = () => {
    setIsACHModalShown(false)
    setSelectedBankType(null)
  }

  const handleCardModalSave = async data => {
    try {
      await dispatch(saveUserCard(data))
      await dispatch(getUserCards())
      setIsModalShown(false)
    } catch (error) {
      // Error handling is done in the ConfigureCardModal
    }
  }

  const handleBankModalSave = async () => {
    try {
      // The AddBankAccount handles the save logic internally
      // We just need to refresh the bank accounts list
      await dispatch(getBankAccounts())
      setIsACHModalShown(false)
      setSelectedBankType(null)
    } catch (error) {
      // Error handling is done in the AddBankAccount
    }
  }

  const handleDecline = () => {
    if (caseId) {
      dispatch(updateClientCaseStatus(caseId, 'rejected'))
        .then(() => {
          onClose()
        })
        .catch(() => {
          // Error handling is done in the action
        })
    }
  }

  const handleApprove = () => {
    if (caseId) {
      dispatch(updateClientCaseStatus(caseId, 'active'))
        .then(() => {
          onClose()
        })
        .catch(() => {
          // Error handling is done in the action
        })
    }
  }

  const onEditCard = async id => {
    try {
      const data = await dispatch(getUserCard(id))
      setCardData(data)
      setIsModalShown(true)
    } catch (e) {
      // Error handling is done in the action
    }
  }

  const onSetDefault = async id => {
    const data = {
      is_default: true,
    }
    try {
      await dispatch(updateUserCard(id, data))
      dispatch(getUserCards())
      dispatch(getBankAccounts()) // Also refresh bank accounts to update their default status
    } catch (e) {
      // Error handling is done in the action
    }
  }

  const onDeleteCard = async id => {
    try {
      await dispatch(deleteUserCard(id))
      dispatch(getUserCards())
    } catch (e) {
      // Error handling is done in the action
    }
  }

  const onSetDefaultBank = async uuid => {
    try {
      await dispatch(setBankAccountDefault(uuid))
      dispatch(getBankAccounts())
      dispatch(getUserCards()) // Also refresh cards to update their default status
    } catch (e) {
      // Error handling is done in the action
    }
  }

  const onDeleteBank = async paymentMethodId => {
    try {
      await dispatch(deleteBankAccount(paymentMethodId))
      dispatch(getBankAccounts())
    } catch (e) {
      // Error handling is done in the action
    }
  }

  const formatDate = dateString => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })
  }

  const formatAmount = amount => {
    if (!amount) return '$0.00'
    return `${clientCaseDetails?.currency === 'CAD' ? 'C$' : '$'}${parseFloat(amount).toFixed(2)}`
  }

  const getBillingFrequencyText = (number, type) => {
    if (!number || !type) return 'N/A'
    const typeText = type === 1 ? 'week' : 'month'
    return `${number} ${typeText}${number > 1 ? 's' : ''}`
  }

  // Determine button states
  const isStatusPending = clientCaseDetails?.status === 'pending'
  const hasPaymentMethods = (userCards && userCards.length > 0) || (bankAccounts && bankAccounts.length > 0)
  const canApprove = isStatusPending && hasPaymentMethods
  const canDecline = isStatusPending

  if (isLoading || clientCaseDetailsIsLoading || cardsAreLoading) {
    return (
      <Modal open={isOpen} onClose={onClose} className={classes.modal}>
        <div className={classes.paperLoader}>
          <div style={{ padding: 40, textAlign: 'center' }}>
            <ColorCircularProgress />
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <>
      <Modal open={isOpen} handleClose={onClose} maxWidth={'xl'} dialogClasses={classes.modal}>
        <div className={classes.paper}>
          <div className={classes.content}>
            {/* Header */}
            <div className={classes.header}>
              <Typography className={classes.title}>Recurring payment approval</Typography>
              <div
                className={classNames(classes.statusBadge, {
                  [classes.statusPending]: clientCaseDetails?.status === 'pending',
                  [classes.statusActive]: clientCaseDetails?.status === 'active',
                  [classes.statusRejected]: clientCaseDetails?.status === 'rejected',
                })}
              >
                {getStatusLabel(clientCaseDetails?.status)}
              </div>
            </div>

            {/* Case Details */}
            <div className={classes.body}>
              <div className={classes.leftSection}>
                <div className={classes.infoRow}>
                  <Typography className={classes.label} variant="body2">
                    Provider name:
                  </Typography>
                  <Typography className={classes.value} variant="body2">
                    {clientCaseDetails?.pro_name || 'N/A'}
                  </Typography>
                </div>
                <div className={classes.infoRow}>
                  <Typography className={classes.label} variant="body2">
                    Firm name:
                  </Typography>
                  <Typography className={classes.value} variant="body2">
                    {clientCaseDetails?.firm_name || '-'}
                  </Typography>
                </div>
                <div className={classes.infoRow}>
                  <Typography className={classes.label} variant="body2">
                    Project name:
                  </Typography>
                  <Typography className={classes.value} variant="body2">
                    {clientCaseDetails?.name || 'N/A'}
                  </Typography>
                </div>
              </div>

              <div className={classes.rightSection}>
                <div className={classes.infoColumn}>
                  <div className={classes.infoRow}>
                    <Typography className={classes.label} variant="body2">
                      Amount:
                    </Typography>
                    <Typography className={classes.value} variant="body2">
                      {formatAmount(clientCaseDetails?.amount)}
                    </Typography>
                  </div>
                  <div className={classes.infoRow}>
                    <Typography className={classes.label} variant="body2">
                      Repeat billing every:
                    </Typography>
                    <Typography className={classes.value} variant="body2">
                      {getBillingFrequencyText(
                        clientCaseDetails?.billing_frequency_number,
                        clientCaseDetails?.billing_frequency_type
                      )}
                    </Typography>
                  </div>
                  <div className={classes.infoRow}>
                    <Typography className={classes.label} variant="body2">
                      First billing date:
                    </Typography>
                    <Typography className={classes.value} variant="body2">
                      {formatDate(clientCaseDetails?.first_billing_date)}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Methods */}
            <div className={classes.paymentMethodsSection}>
              <div className={classes.paymentMethodsHeader}>
                <Typography className={classes.paymentMethodsTitle}>Payment methods</Typography>
                <div className={classes.addButtons}>
                  <Tooltip
                    tooltipClass={classes.tooltip}
                    title={sortedCards?.length >= 5 ? 'The maximum number of credit cards has been reached.' : ''}
                  >
                    <Button
                      className={classes.addButton}
                      variant="secondary"
                      size="small"
                      onClick={handleAddCard}
                      disabled={userCards?.length >= 5}
                    >
                      add credit card
                    </Button>
                  </Tooltip>
                  {sortedBanks?.length >= 5 ? (
                    <Tooltip
                      tooltipClass={classes.tooltip}
                      title={'The maximum number of bank accounts has been reached.'}
                    >
                      <Button variant="secondary" size="small" disabled={true} className={classes.addButton}>
                        add bank account
                      </Button>
                    </Tooltip>
                  ) : (
                    <SplitButton
                      variant="secondary"
                      color="secondary"
                      size="small"
                      onSelect={bankType => handleAddBankAccount(bankType)}
                      options={['US bank account', 'Canadian bank account']}
                      classes={{
                        buttons: classes.manageInnerButtons,
                        iconButton: classes.manageIconButton,
                      }}
                    >
                      <Icon style={classes.buttonIcon} /> add bank account
                    </SplitButton>
                  )}
                </div>
              </div>
              {hasPaymentMethods ? (
                <>
                  {!!(userCards && userCards.length) && (
                    <div style={{ marginBottom: 16 }}>
                      <Typography variant="subtitle1" style={{ marginBottom: 16, color: colors.BASIC.COLOR[70] }}>
                        CARDS
                      </Typography>
                      <div className={classes.cards}>
                        {sortedCards.map(card => (
                          <CardRow
                            cardData={card}
                            key={card.uuid}
                            onEditCard={id => onEditCard(id)}
                            onSetDefault={id => onSetDefault(id)}
                            onDeleteCard={id => onDeleteCard(id)}
                            showConfigurationButton
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {!!(bankAccounts && bankAccounts.length) && (
                    <div style={{ marginBottom: 16 }}>
                      <Typography variant="subtitle1" style={{ marginBottom: 16, color: colors.BASIC.COLOR[70] }}>
                        BANK ACCOUNTS
                      </Typography>
                      <div className={classes.cards}>
                        {sortedBanks.map(bank => (
                          <BankRow
                            bankData={bank}
                            key={bank.payment_method_id}
                            onSetDefault={id => onSetDefaultBank(id)}
                            onDeleteBank={id => onDeleteBank(id)}
                            showConfigurationButton
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className={classes.noPaymentMethodsText}>
                  Add and manage your payment methods using our secure payment system.
                </div>
              )}
            </div>
            <div className={classes.actionButtons}>
              <Button
                className={classes.declineButton}
                onClick={handleDecline}
                disabled={!canDecline || clientCaseStatusIsUpdating}
              >
                <Icon iconClass="las la-times" className={classes.buttonIcon} />
                Decline
              </Button>
              <Button
                className={classes.approveButton}
                onClick={handleApprove}
                disabled={!canApprove || clientCaseStatusIsUpdating}
              >
                <Icon iconClass="las la-check" className={classes.buttonIcon} />
                Approve
              </Button>
            </div>
          </div>
        </div>
      </Modal>
      <ConfigureCardModal
        isOpen={isModalShown}
        onClose={handleCardModalClose}
        onSave={handleCardModalSave}
        mode={CONSTANTS.MODE.ADD}
        clientPaymentSecretKey={clientPaymentSecretKey}
        clientSecretIsLoading={clientSecretIsLoading}
        cardData={cardData}
      />

      {isACHModalShown && (
        <AddBankAccount isOpen={isACHModalShown} onClose={handleACHModalClose} bankType={selectedBankType} />
      )}
    </>
  )
}

export default ClientPaymentApprovalModal
